import { InputHTMLAttributes, useId, forwardRef } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, disabled, className = '', ...props }, ref) => {
    const generatedId = useId();
    const inputId = props.id ?? generatedId;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const inputClasses = `
      h-10 px-3 w-full text-[14px] text-[#4A5568] rounded
      transition-all border
      placeholder:text-[#718096]
      focus:outline-none
      disabled:bg-[#F7FAFC] disabled:opacity-50 disabled:cursor-not-allowed
      ${error 
        ? 'border-[#DC2626] focus:border-[#DC2626]' 
        : 'border-[#E2E8F0] focus:border-[#1B365D] focus:border-2'
      }
      ${className}
    `;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[13px] font-semibold text-[#4A5568] mb-1.5"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={inputClasses}
          disabled={disabled}
          aria-describedby={
            error ? errorId : helperText ? helperId : undefined
          }
          aria-invalid={error ? true : undefined}
          {...props}
        />
        {error && (
          <p id={errorId} className="text-[11px] text-[#DC2626] mt-1" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={helperId} className="text-[11px] text-[#718096] mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
